"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var _a;
Object.defineProperty(exports, "__esModule", { value: true });
exports.longDateFormatWithTime = exports.monthDayYearUtc = exports.monthDayYear = exports.longDateTimeFormatWithTimeZone = exports.shortDateTimeFormatWithTimeZone = exports.shortDateFormat = exports.standardUTCDateFormat = exports.standardDateFormat = exports.formatDateWithStyle = exports.formatDate = exports.DateFormatStyle = void 0;
var date_fns_1 = require("date-fns");
var date_fns_tz_1 = require("date-fns-tz");
var utils_1 = require("./utils");
/**
 * We will always format time the same way in every date style.
 */
var TIME_OPTIONS = {
    hour12: true,
    hour: 'numeric',
    minute: 'numeric',
    timeZoneName: 'short',
};
var DateFormatStyle;
(function (DateFormatStyle) {
    /**
     * Example: 1/1/2000 (Localised)
     *
     * All of the date formatters use the Intl API which allows us to render dates
     * according to the users language. For example, anyone using US English it will see month first,
     * whereas anyone outside of the US will see day first.
     */
    DateFormatStyle["STANDARD"] = "STANDARD";
    /**
     * Example: 1/1
     *
     * All of the date formatters use the Intl API which allows us to render dates
     * according to the users language. For example, anyone using US English it will see month first,
     * whereas anyone outside of the US will see day first.
     */
    DateFormatStyle["SHORT"] = "SHORT";
    /**
     * Example: June 28th 1971 or 28th June 1971
     *
     * All of the date formatters use the Intl API which allows us to render dates
     * according to the users language. For example, anyone using US English it will see month first,
     * whereas anyone outside of the US will see day first.
     */
    DateFormatStyle["MEDIUM"] = "MEDIUM";
    /**
     * Tuesday Jun 16, 2020 11:29 PM PDT
     *
     * All of the date formatters use the Intl API which allows us to render dates
     * according to the users language. For example, anyone using US English it will see month first,
     * whereas anyone outside of the US will see day first.
     */
    DateFormatStyle["LONG"] = "LONG";
})(DateFormatStyle = exports.DateFormatStyle || (exports.DateFormatStyle = {}));
/**
 * Maps a DateFormatStyle to set of options that can be passed to Intl.DateTimeFormat().
 */
var FORMAT_OPTIONS = (_a = {},
    _a[DateFormatStyle.SHORT] = {
        day: 'numeric',
        month: 'numeric',
    },
    _a[DateFormatStyle.MEDIUM] = {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    },
    _a[DateFormatStyle.STANDARD] = {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    },
    _a[DateFormatStyle.LONG] = {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        weekday: 'long',
    },
    _a);
/**
 * Returns the options that can be passed to Intl.DateTimeFormat().
 */
function createFormatterOptions(options) {
    var _a = options.style, style = _a === void 0 ? DateFormatStyle.STANDARD : _a, timezone = options.timezone, includeTime = options.includeTime;
    var timeOptions = includeTime ? TIME_OPTIONS : {};
    return __assign(__assign(__assign({}, FORMAT_OPTIONS[style]), timeOptions), { timeZone: timezone });
}
/**
 * Format a date-like string or object into a set format. These use the Intl library so they
 * are formatted according to the users language. There are a fix number of date styles we can
 * use so that they are mostly consistent in the UI.
 *
 * This function will require a timezone to be passed in so that it is not ambiguious about
 * which way the date will be used.
 */
function formatDate(date, options) {
    var timezone = options.timezone, _a = options.style, style = _a === void 0 ? DateFormatStyle.STANDARD : _a;
    if ((0, utils_1.isDateString)(date)) {
        return formatDateWithStyle((0, date_fns_tz_1.toDate)(date, {
            timeZone: timezone,
        }), __assign(__assign({}, options), { style: style }));
    }
    var isNativeDate = (0, date_fns_1.isDate)(date) && (0, date_fns_1.isValid)(date);
    var isTimestamp = typeof date === 'number';
    if ((0, utils_1.isISOString)(date) || isNativeDate || isTimestamp) {
        var dateObj = (0, date_fns_tz_1.toDate)(date, { timeZone: timezone });
        return formatDateWithStyle(dateObj, __assign(__assign({}, options), { style: style }));
    }
    throw new Error("Invalid date: ".concat(date));
}
exports.formatDate = formatDate;
/**
 * This is an internal function that will only accept a date.
 */
function formatDateWithStyle(date, options) {
    var timezone = options.timezone, locale = options.locale, _a = options.style, style = _a === void 0 ? DateFormatStyle.STANDARD : _a;
    var formatter = new Intl.DateTimeFormat(locale, __assign(__assign({}, createFormatterOptions(__assign(__assign({}, options), { style: style }))), { timeZone: timezone }));
    return formatter.format(date);
}
exports.formatDateWithStyle = formatDateWithStyle;
/**
 * Legacy formatting functions. These are kept for backwards compatibility.
 */
/**
 * @deprecated Use formatDate instead
 */
function standardDateFormat(date) {
    return formatDate(date, {
        style: DateFormatStyle.STANDARD,
        timezone: (0, utils_1.getLocalTimezone)(),
        locale: 'en-US',
    });
}
exports.standardDateFormat = standardDateFormat;
/**
 * @deprecated Use formatDate instead
 */
function standardUTCDateFormat(date) {
    return formatDate(date, {
        style: DateFormatStyle.STANDARD,
        timezone: utils_1.UTC_TIMEZONE,
        locale: 'en-US',
    });
}
exports.standardUTCDateFormat = standardUTCDateFormat;
/**
 * @deprecated Use formatDate instead
 */
function shortDateFormat(date) {
    return formatDate(date, {
        style: DateFormatStyle.SHORT,
        timezone: (0, utils_1.getLocalTimezone)(),
        locale: 'en-US',
    });
}
exports.shortDateFormat = shortDateFormat;
/**
 * @deprecated Use formatDate instead
 */
function shortDateTimeFormatWithTimeZone(date) {
    return formatDate(date, {
        style: DateFormatStyle.SHORT,
        includeTime: true,
        timezone: (0, utils_1.getLocalTimezone)(),
        locale: 'en-US',
    });
}
exports.shortDateTimeFormatWithTimeZone = shortDateTimeFormatWithTimeZone;
/**
 * @deprecated Use formatDate instead
 */
function longDateTimeFormatWithTimeZone(date) {
    return formatDate(date, {
        style: DateFormatStyle.LONG,
        includeTime: true,
        timezone: (0, utils_1.getLocalTimezone)(),
        locale: 'en-US',
    });
}
exports.longDateTimeFormatWithTimeZone = longDateTimeFormatWithTimeZone;
/**
 * @deprecated Use formatDate instead
 */
function monthDayYear(date) {
    if (!date)
        return '';
    return formatDate(date, {
        style: DateFormatStyle.MEDIUM,
        includeTime: false,
        timezone: (0, utils_1.getLocalTimezone)(),
        locale: 'en-US',
    });
}
exports.monthDayYear = monthDayYear;
/**
 * @deprecated Use formatDate instead
 */
function monthDayYearUtc(date) {
    return formatDate(date, {
        style: DateFormatStyle.MEDIUM,
        includeTime: false,
        timezone: utils_1.UTC_TIMEZONE,
        locale: 'en-US',
    });
}
exports.monthDayYearUtc = monthDayYearUtc;
/**
 * @deprecated Use formatDate instead
 */
function longDateFormatWithTime(date) {
    return formatDate(date, {
        style: DateFormatStyle.LONG,
        includeTime: false,
        timezone: (0, utils_1.getLocalTimezone)(),
        locale: 'en-US',
    });
}
exports.longDateFormatWithTime = longDateFormatWithTime;
